import {ComponentType, Fragment, ReactElement, ReactNode} from 'react'
import classNames from 'classnames'
import {Trans, WithNamespaces, withNamespaces} from 'react-i18next'
import {Grid, Typography} from '@material-ui/core'
import {formatNumber, getPercentFormatter} from '../../../../../../../../../common/utils'
import messages from '../../../../../../../../../assets/messages'
import Amount from '../../../../../../../../Common/Amount'
import {BrokereeCopyTradingOfferStatistics} from '../../../../../../types'
import {useStyles} from './styles'

type StatisticsPropsBase = {
  statistics: BrokereeCopyTradingOfferStatistics
}

type StatisticsProps = StatisticsPropsBase & WithNamespaces

const Component = (props: StatisticsProps): ReactElement => {
  const {lng, statistics} = props
  const {totalTrades, winningTrades, losingTrades, totalProfit, currency, roi} = statistics

  const classes = useStyles()

  const formatRoi = (roi: number): Intl.NumberFormatPart[] => getPercentFormatter(lng).formatToParts(roi / 100)

  const renderFormattedRoi = (part: Intl.NumberFormatPart): ReactNode => {
    const {type, value} = part

    if (type === 'percentSign') return <span className={classes.roiPercent}>{value}</span>
    return value
  }

  return (
    <Grid item container className={classes.container} xs={12} md={8} lg={7}>
      <Grid item className={classes.item} xs={6} sm="auto">
        <Typography variant='caption'>
          <Trans {...messages.totalTrades} />
        </Typography>
        <Typography variant='body1' className={classes.itemValue}>
          {formatNumber(lng, totalTrades)}
        </Typography>
      </Grid>

      <Grid item className={classes.item} xs={6} sm="auto">
        <Typography variant='caption'>
          <Trans {...messages.totalWinningTradesCount} />
        </Typography>
        <Typography variant='body1' className={classNames(classes.itemValue, classes.profit)}>
          {formatNumber(lng, winningTrades)}
        </Typography>
      </Grid>

      <Grid item className={classes.item} xs={6} sm="auto">
        <Typography variant='caption'>
          <Trans {...messages.totalLosingTradesCount} />
        </Typography>
        <Typography variant='body1' className={classNames(classes.itemValue, classes.loss)}>
          {formatNumber(lng, losingTrades)}
        </Typography>
      </Grid>

      <Grid item className={classes.item} xs={6} sm="auto">
        <Typography variant='caption'>
          <Trans {...messages.closedProfit} />
        </Typography>
        {currency != null ? (
          <Amount
           value={totalProfit}
           currency={currency}
           locale='en-US'
           classes={{
             root: totalProfit < 0 ? classes.loss : classes.profit,
             currency: classes.amountCurrency,
             value: classes.amountValue,
             fraction: classes.amountFraction
           } as Record<string, string>}
         />
        ) : '-'}
      </Grid>

      {roi != null ? (
        <Grid item className={classes.item} xs={6} sm="auto">
          <Typography variant='caption'>
            <Trans {...messages.roi} />
          </Typography>
          <Typography variant='body1' className={classes.itemValue}>
            {formatRoi(roi).map((part, index) =>
              <Fragment key={index}>{renderFormattedRoi(part)}</Fragment>
            )}
          </Typography>
        </Grid>
      ) : null}
    </Grid>

  )
}

export const Statistics: ComponentType<StatisticsPropsBase> = withNamespaces()(Component)

